import { useEffect, useState } from "react";
import { useContext } from "react";
import FlowContext from "../../context/FlowContext";
import KnowledgeBasesContext from "../../context/KnowledgeBasesContext";
import Accordion, { AccordionItem } from '../../components/@base/Accordion';
import Input from '../../components/@base/Input';
import Button from '../../components/@base/Button';
import { DataSources } from "./DataSources";
import DeleteConfirmation from "../../components/DeleteConfirmation/DeleteConfirmation.js";
import Loading from "../../components/Loading/Loading";
import SaveContext from "../../context/SaveContext";

/**
 * Componente que é responsável pela exibição da lista de bases de conhecimento.
 * 
 * @param {*} props 
 */
export const KnowledgeBasesList = (props) => {

    const saveContext = useContext(SaveContext);

    // Contexto que contém os métodos para obter, atualizar e remover
    // bases de conhecimento, datasources e etc.
    const context = useContext(KnowledgeBasesContext);

    // Contexto que contém qual o chatbot selecionado pelo usuário.
    // Usado para atualizar a View do componente toda vez
    // que o chatbot for modificado. Ver useEffect().
    const flowContext = useContext(FlowContext);

    // Items que serão exibidos na listagem.
    // As KnowledgeBases são encapsuladas em itens, que guardam
    // o estado de cada KnowledgeBase exibida na lista.
    const [items, setItems] = useState([]);

    // Lista de tipos de DataSource.
    // Usado para determinar quais tipos de arquivo são aceitos, tamanho e etc.
    const [dataSourceTypes, setDataSourceTypes] = useState([]);

    // Mensagens de erro na validação dos dados da base de conhecimento.
    const [errorMessages, setErrorMessages] = useState([]);

    // Usado para exibir o overlay de carregamento toda vez que for
    // realizada uma requisição à API.
    const [showLoading, setShowLoading] = useState(false);

    // Estado que determina como a mensagem de confirmação para remoção
    // será exibida.
    const [deleteConfirmation, setDeleteConfirmation] = useState(
        {
            knowledgeBase: null,
            dataSource: null,
            show: false, 
            content: "Atenção! Tem certeza que deseja excluir o item selecionado?", 
            title: "Confirmação", 
            confirm: () => {}, 
            cancel: () => { setDeleteConfirmation({ ...deleteConfirmation, show: false})}
        }
    );

    // Obter novamente a lista de bases de conhecimento toda vez
    // que mudar o chatbot que está sendo visualizado.
    useEffect(() => {
        fetch();
        fetchDataSourceTypes();
    }, [flowContext.activeBotId]);

    /**
     * Obtém a lista de tipos de fonte de dados e
     * guarda para repassar aos componentes filhos.
     * 
     */
    const fetchDataSourceTypes = async () => {
        const types = await context.fetchDataSourceTypes();
        setDataSourceTypes(types);
    };

    /**
     * Obtém a lista de bases de conhecimento da API, atualizando o contexto.
     * 
     * Transforma a lista de bases de conhecimento em uma lista de items,
     * para ser usada pelo componente.
     * 
     */
    const fetch = async (lastCreatedItem = undefined) => {
        setShowLoading(true);

        const fetched = await context.fetch();

        const newItems = fetched.map((knowledgeBase) => {

            // Verificamos se existe um último item criado, se sim,
            // devemos deixar o AccordionItem aberto para a edição imediata.
            // Esta situação ocorre quando o usuário clica no botão para criar
            // uma nova base de conhecimento.
            let active = false;

            if (lastCreatedItem && lastCreatedItem.knowledgeBase) {
                if (knowledgeBase.id === lastCreatedItem.knowledgeBase.id) {
                    active = lastCreatedItem.active;
                }
            }

            return {
                knowledgeBase: knowledgeBase,
                active: active
            };
        });

        setItems([...newItems]);
        setShowLoading(false);
    }

    /**
     * Trata o evento que é lançado pelo componente do item da lista, informando
     * que uma base de conhecimento foi atualizada.
     * 
     * @param {*} knowledgeBase Dados da base de conhecimento.
     */
    const handleKnowledgeBaseChanged = async (knowledgeBase) => {
        saveContext.beginSaving();
        await context.updateKnowledgeBase(knowledgeBase);
        saveContext.finishSaving();

        await fetch();
    };

    /**
     * Trata o evento de clique para criar uma nova base de conhecimento.
     * Apenas adiciona uma nova base de conhecimento na lista com dados padrão.
     * 
     */
    const handleCreateKnowledgeBase = async () => {
        const newItem =  {
            knowledgeBase: {
                name: "Nova Base de Conhecimento",
                description: "Minha nova base de conhecimento",
                dataSources: []
            },
            active: true
        };

        saveContext.beginSaving();
        newItem.knowledgeBase = await context.createKnowledgeBase(newItem.knowledgeBase);
        saveContext.finishSaving();

        await fetch(newItem);
    };

    /**
     * Trata o evento que solicita a remoção de uma base de conhecimento.
     * Exibe a janela de confirmação de exclusão apenas.
     * 
     * @param {*} knowledgeBase Base que deve ser removida.
     */
    const handleDeleteKnowledgeBase = async (knowledgeBase) => {
        setDeleteConfirmation({
            ...deleteConfirmation, 
            show: true, 
            confirm: () => handleConfirmKnowledgeBaseDeletion(knowledgeBase)
        });
    };

    /**
     * Handler utilizado pelo callback de confirmação da exclusão, que está no método
     * handleDeleteKnowledgeBase().
     * 
     * @param {*} knowledgeBase Base de conhecimento que deve ser removida.
     */
    const handleConfirmKnowledgeBaseDeletion = async (knowledgeBase) => {
        try {
            setDeleteConfirmation({...deleteConfirmation, show: false});

            saveContext.beginSaving();
            await context.deleteKnowledgeBase(knowledgeBase);
            saveContext.finishSaving();

            await fetch();
        } catch(error) {
            setErrorMessages(['Não é possível remover essa base de conhecimento. Verifique se ela não está relacionada a algum componente.']);
        }
    };

    /**
     * Trata o evento que requisita a remoção de uma fonte de dados.
     * 
     * Este evento é lançado pelo componente KnowledgeBaseListItem.
     * Apenas exibe o alerta pedindo a confirmação da exclusão.
     * 
     * @param {*} knowledgeBase Base de conhecimento que contém a fonte de dados.
     * @param {*} dataSource Fonte de dados que deve ser removida.
     */
    const handleDeleteDataSource = async (knowledgeBase, dataSource) => {
        setDeleteConfirmation({
            ...deleteConfirmation, 
            show: true, 
            confirm: () => handleConfirmDataSourceDeletion(knowledgeBase, dataSource)
        });
    };

    /**
     * Handler utilizado pelo callback de confirmação da exclusão, que está no método
     * handleDeleteDataSource().
     * 
     * @param {*} knowledgeBase Base de conhecimento que contém a fonte de dados a ser removida.
     * @param {*} dataSource  Fonte de dados que deve ser removida.
     */
    const handleConfirmDataSourceDeletion = async (knowledgeBase, dataSource) => {
        setDeleteConfirmation({...deleteConfirmation, show: false});

        saveContext.beginSaving();
        await context.deleteDataSource(knowledgeBase.id, dataSource);
        saveContext.finishSaving();

        await fetch();
    };

    /**
     * Trata o evento que é lançado pelo componente KnowledgeBaseListItem, solicitando
     * a criação de um DataSource.
     * 
     * @param {*} knowledgeBase Base de conhecimento que deve conter o DataSource criado.
     * @param {*} request Dados que serão usados para criar o DataSource.
     */
    const handleCreateDataSource = async (knowledgeBase, request) => {
        saveContext.beginSaving();
        await context.createDataSource(knowledgeBase.id, request);
        saveContext.finishSaving();

        await fetch();
    };

    return (
        <div>
            {showLoading ? <Loading message="Aguarde" /> : null}
            
            { deleteConfirmation.show ? 
              <DeleteConfirmation
                content={deleteConfirmation.content}
                title={deleteConfirmation.title}
                confirmationClick={() => deleteConfirmation.confirm()}
                cancelationClick={() => deleteConfirmation.cancel()} />
              : null
            }


            <br />
            <Button
                primary
                onClick={handleCreateKnowledgeBase}>
                <small>Criar Base de Conhecimento</small>
            </Button>
            <br /><br />
            <span style={{color: 'red', fontSize: '11pt'}}>{errorMessages}</span>
            <Accordion>
                {
                    items.map((item, index) => (
                        <AccordionItem active={item.active} key={index}>
                            <div>
                                <div>
                                    <strong>
                                        {item.knowledgeBase.name}
                                    </strong>
                                </div>
                                <small>{item.knowledgeBase.description}</small>
                                <br></br>
                                {item.knowledgeBase.referencedBy && item.knowledgeBase.referencedBy.length >0 ? 
                                <small>
                                    Utilizada por {item.knowledgeBase.referencedBy}
                                </small>: null }
                            </div>
                            
                            <KnowledgeBasesListItem 
                                onCreateDataSource={(knowledgeBase, request) => handleCreateDataSource(knowledgeBase, request)}
                                onDeleteDataSource={(knowledgeBase, dataSource) => handleDeleteDataSource(knowledgeBase, dataSource)}
                                onDelete={(event) => handleDeleteKnowledgeBase(event)}
                                onChange={(event) => handleKnowledgeBaseChanged(event)} 
                                knowledgeBase={item.knowledgeBase} 
                                dataSourceTypes={dataSourceTypes}
                            />

                        </AccordionItem>
                    ))
                }
            </Accordion>            
        </div>
    );
};

/**
 * Componente que é responsável por exibir os dados de uma base de conhecimento
 * dentro da listagem de bases de conhecimento.
 * 
 * O próprio componente faz a atualização da base de conhecimento na API, mas
 * notifica os componentes da hierarquia de que houve a atualização.
 * 
 * @param {*} props 
 */
export const KnowledgeBasesListItem = (props) => {
    const [validation, setValidation] = useState({name: true, description: true});
    const [knowledgeBase, setKnowledgeBase] = useState(props.knowledgeBase);
    const [dataSourceTypes, setDataSourceTypes] = useState(props.dataSourceTypes);
    const onDeleteDataSource = props.onDeleteDataSource;
    const onCreateDataSource = props.onCreateDataSource;
    const onChange = props.onChange;
    const onDelete = props.onDelete;

    // Atualiza a base de conhecimento e os tipos de datasources toda vez que esses valores forem
    // modificados nas propriedades.
    useEffect(() => {
        setKnowledgeBase(props.knowledgeBase);
        setDataSourceTypes(props.dataSourceTypes);
    }, [props.knowledgeBase, props.dataSourceTypes]);

    // Faz a validação dos campos toda vez que os dados da base de conhecimento são modificados.
    // O estado dos campos modifica conforme os valores são modificados para valores inválidos.
    useEffect(() => {
        setValidation({
            name: knowledgeBase.name && knowledgeBase.name.length > 0,
            description: knowledgeBase.description && knowledgeBase.description.length > 0
        });
    }, [knowledgeBase]);

    /**
     * Trata o evento de clique do botão que solicita a atualização.
     * 
     * Realiza a atualização na API e verifica chama o callback OnChange
     * para notificar os componentes da hierarquia de que houve uma modificação
     * na base de conhecimento.
     * 
     */
    const handleUpdate = async () => {
        onChange?.({
            id: knowledgeBase.id,
            name: knowledgeBase.name,
            description: knowledgeBase.description
        });
    };

    /**
     * Trata o evento que é lançado pelo componente de DataSources,
     * solicitando a criação de um novo DataSource.
     * 
     * O evento é repassado para o componente acima na hierarquia,
     * que é quem realizará de fato a criação.
     * 
     * @param {*} request Fonte de dados.
     */
    const handleCreateDataSource = (request) => {
        onCreateDataSource?.(knowledgeBase, request);
    };

    /**
     * Trata o evento que solicita a remoção de uma fonte de dados.
     * 
     * O evento é repassado para o componente acima na hierarquia,
     * que é quem realizará de fato a remoção.
    */
    const handleDeleteDataSource = (dataSource) => {
        onDeleteDataSource?.(knowledgeBase, dataSource);
    };

    /**
     * Trata o evento que solicita a remoção da base de conhecimento.
     * 
     * O evento é repassado para o componente acima na hierarquia,
     * que é quem realizará de fato a remoção.
    */
    const handleDelete = async () => {
        onDelete?.(knowledgeBase);
    };

    return (
        <div>
            <div>
                <Input
                    invalid={!validation.name}
                    label="Nome"
                    width={'100%'}
                    placeholder="Informe o nome da base de conhecimento"
                    value={knowledgeBase.name}
                    labelWidth={55}
                    onChange={(event) => setKnowledgeBase({...knowledgeBase, name: event.target.value})}
                />
                <br/>
                <Input
                    invalid={!validation.description}
                    label="Descrição"
                    width={'100%'}
                    placeholder="Informe a descrição da base de conhecimento"
                    value={knowledgeBase.description}
                    labelWidth={55}
                    onChange={(event) => setKnowledgeBase({...knowledgeBase, description: event.target.value}) }
                />
                <br/>
                <div className="row">
                    <Button
                        disabled={!validation.name || !validation.description }
                        small
                        primary
                        onClick={handleUpdate}>
                        <small>Atualizar</small>
                    </Button>&nbsp;
                    <Button
                        disabled={knowledgeBase.referencedBy != null && knowledgeBase.referencedBy.length > 0}
                        small
                        danger
                        onClick={handleDelete}>
                        <small>Remover</small>
                    </Button>
                </div>
            </div>
            <div style={{marginTop: '40px'}}>
                <DataSources 
                    types={dataSourceTypes}
                    dataSources={knowledgeBase.dataSources} 
                    onCreate={handleCreateDataSource} 
                    onDelete={handleDeleteDataSource} />
            </div>
        </div>
    );
};